import {
    Button,
    Checkbox,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    FormControlLabel,
    TextField,
} from '@mui/material';
import { useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import MfaTypes from '../MfaTypes';
import { startAuthentication } from '@simplewebauthn/browser';
import { authActions, authSelectors } from '../modules/auth';

export default function MfaModal(props) {
    const { mfaType, recipient, hasPasskey, hasTotp, hasEmail } = useSelector(authSelectors.mfaState);
    const dispatch = useDispatch();

    // Guards against a second passkey ceremony starting while one is in flight.
    // generateAuthenticationOptions overwrites a single per-user challenge slot, so a
    // duplicate invocation regenerates the challenge after the authenticator already
    // signed the first one — producing a "challenge mismatch" verification failure.
    const passkeyInFlight = useRef(false);

    const dialogText = {
        [MfaTypes.TOTP]: "Enter the 6-digit security code from your authenticator app.",
        [MfaTypes.PASSKEY]: "Use your passkey to authenticate.",
        [MfaTypes.EMAIL]: `Enter the 6-digit code sent to: ${recipient}`,
    }

    const {
        open,
        onClose,
        onChange,
        onBlur,
        setFieldValue,
        values,
        hideRemember,
        error,
        helperText,
        handleSubmit: handleTotpSubmit,
        skipResubmitOnSwitch, // For SAML flow - don't resubmit when switching MFA methods
    } = props;

    const handlePasskeySubmit = async () => {
        // Ignore re-entrant clicks: a second ceremony would regenerate the challenge
        // mid-flight and invalidate the response the authenticator is about to return.
        if (passkeyInFlight.current) return;
        passkeyInFlight.current = true;
        try {
            // .unwrap() so a failed thunk throws the real error into our catch instead of
            // resolving to a rejected action (which would TypeError on resp.payload.data).
            const resp = await dispatch(authActions.generateAuthenticationOptions()).unwrap();
            if (!resp?.data) {
                // Surface to the user rather than throwing into the catch below
                // (which only logs) — otherwise the modal just sits unresponsive.
                // The finally block still resets passkeyInFlight.
                dispatch(authActions.setErrorMessage('No authentication options returned from the server.'));
                return;
            }
            const authResp = await startAuthentication({optionsJSON: resp.data});

            // Pass the authResp directly to backend for verification.
            // setFieldValue is async — await it so mfaCode is committed before we submit,
            // otherwise handleTotpSubmit reads a stale (null) value on the first attempt.
            await setFieldValue('mfaCode', authResp);
            handleTotpSubmit(); // Trigger submission using Formik's internal state
        } catch (err) {
            // Handle user cancellation or timeout - NotAllowedError
            if (err.name === 'NotAllowedError') {
                // User cancelled or timed out - do nothing, they can try again
                console.log('Passkey authentication cancelled by user');
                return;
            }
            // BLU-1508: any other failure — a rejected generate/verify thunk (now
            // surfaced here because of the .unwrap() above) or a non-cancel WebAuthn
            // error — used to die silently at this console.error, leaving a dead
            // "Authenticate with Passkey" button and no feedback. Surface a toast so
            // the user knows the attempt failed and can retry or switch methods.
            // LocalAlert (mounted by the parent Authentication route) renders this in
            // both the login and SAML-callback flows since MfaModal is shared.
            console.error('Passkey authentication error:', err);
            dispatch(authActions.setErrorMessage(
                'Passkey authentication failed. Please try again or use another sign-in method.'
            ));
        } finally {
            passkeyInFlight.current = false;
        }
    }

    const handleSwitchToTotp = () => {
        // Switch to TOTP for this session
        dispatch(authActions.setMfaType(MfaTypes.TOTP));
        // Clear any previous verification code
        setFieldValue('verificationCode', '');
        setFieldValue('mfaMethodOverride', MfaTypes.TOTP);
        // For SAML flow, don't resubmit - just switch the UI
        // For regular login, resubmit to get TOTP challenge from backend
        if (!skipResubmitOnSwitch) {
            handleTotpSubmit();
        }
    }

    const handleSwitchToEmail = () => {
        // Switch to EMAIL for this session
        dispatch(authActions.setMfaType(MfaTypes.EMAIL));
        // Clear any previous verification code
        setFieldValue('verificationCode', '');
        // Re-submit the login to get EMAIL challenge
        setFieldValue('mfaMethodOverride', MfaTypes.EMAIL);
        handleTotpSubmit();
    }

    const handleSwitchToPasskey = () => {
        // Switch to PASSKEY for this session
        dispatch(authActions.setMfaType(MfaTypes.PASSKEY));
        // Clear any previous verification code
        setFieldValue('verificationCode', '');
        // Re-submit the login to get PASSKEY challenge
        setFieldValue('mfaMethodOverride', MfaTypes.PASSKEY);
        handleTotpSubmit();
    }

    const handleSubmit = mfaType === MfaTypes.PASSKEY ? handlePasskeySubmit : handleTotpSubmit;

    // Both EMAIL and TOTP use a text field for 6-digit code
    const showTextField = mfaType === MfaTypes.TOTP || mfaType === MfaTypes.EMAIL;

    return (
        <Dialog open={open} onClose={onClose}>
            <DialogTitle>Confirm Multi-Factor Authentication Code</DialogTitle>
            <DialogContent>
                <DialogContentText sx={{ marginBottom: '1rem' }}>
                    {dialogText[mfaType]}
                </DialogContentText>
                {mfaType === MfaTypes.PASSKEY ? (
                    <Button onClick={handleSubmit}>Authenticate with Passkey</Button>
                ) : (
                    <TextField
                        autoFocus
                        name="verificationCode"
                        onChange={onChange}
                        onBlur={onBlur}
                        onKeyPress={e => { if (e.key === 'Enter') handleSubmit(); }}
                        value={values.verificationCode || ''}
                        error={error}
                        helperText={helperText}
                        label="MFA Code"
                        type="text"
                        variant="standard"
                        autoComplete="off"
                    />
                )}
                {!hideRemember &&
                    <DialogContentText sx={{ marginTop: '1rem', fontSize: 'small' }}>
                        <FormControlLabel
                            control={
                                <Checkbox checked={values.remember} onChange={onChange} name='remember' />
                            }
                            label="Remember this device"
                        />
                    </DialogContentText>
                }
            </DialogContent>
            <DialogActions sx={{ flexWrap: 'wrap', gap: 1 }}>
                {/* Show all available alternative MFA methods */}
                {mfaType !== MfaTypes.PASSKEY && hasPasskey && (
                    <Button type="button" onClick={handleSwitchToPasskey} variant="text">
                        Use Passkey instead
                    </Button>
                )}
                {mfaType !== MfaTypes.TOTP && hasTotp && (
                    <Button type="button" onClick={handleSwitchToTotp} variant="text">
                        Use TOTP instead
                    </Button>
                )}
                {mfaType !== MfaTypes.EMAIL && hasEmail && (
                    <Button type="button" onClick={handleSwitchToEmail} variant="text">
                        Use Email instead
                    </Button>
                )}
                {/* PASSKEY is triggered by its own "Authenticate with Passkey" button above;
                    a second trigger here would start a duplicate ceremony and clobber the challenge. */}
                {mfaType !== MfaTypes.PASSKEY && (
                    <Button type="button" onClick={handleSubmit}>Submit</Button>
                )}
            </DialogActions>
        </Dialog>
    );
}
